import React, { useCallback, useEffect, useMemo, useState } from 'react';

import * as analytics from '@adsk/acs-web-analytics';
import {
  useCurrentAccountId,
  useCurrentProductId,
  useCurrentProjectId,
  useCurrentToolId,
} from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import Checkbox from '@adsk/alloy-react-checkbox';
import Footer, { ClearButton, SelectedCount } from '@adsk/alloy-react-footer';
import { useControlled } from '@adsk/alloy-react-helpers';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';
import theme from '@adsk/alloy-react-theme';
import { InfoPopover } from '@adsk/alloy-react-tooltip';

import { ASSIGNEEPICKER_ANALYTICS } from './helpers/analytics';
import {
  createHelperMappings,
  getTotalSelectedCount,
} from './helpers/include-members';
import { useTranslation } from './i18n';
import {
  AssigneePickerIncludeMembersFooterProps,
  ItemWithGroup,
  RenderSelectedCountProps,
} from './types';

export const defaultRenderSelectedCount = (props: RenderSelectedCountProps) => (
  <SelectedCount {...props} />
);

const AssigneePickerIncludeMembersFooterComponent = ({
  userOptionValueGetter,
  users,
  onClear,
  onBlur,
  includeMembers = false,
  onIncludeMembersChange,
  onSelectedCountChange,
  renderSelectedCount = defaultRenderSelectedCount,
  selectedItems,
  disableClear,
  checkboxLabel,
}: AssigneePickerIncludeMembersFooterProps) => {
  const currentProjectId = useCurrentProjectId();
  const currentProductId = useCurrentProductId();
  const currentToolId = useCurrentToolId();
  const currentAccountId = useCurrentAccountId();

  const { t } = useTranslation();
  const [selectedCount, setSelectedCount] = useState(0);
  const dropdownSelectedItems = selectedItems as ItemWithGroup[];
  const isClearDisabled = disableClear || selectedCount === 0;

  const [isIncludeMembersChecked, setIsIncludeMembersChecked] = useControlled(
    includeMembers,
    includeMembers,
    onIncludeMembersChange,
  );

  const handleIncludeMembersCheckedChange = useCallback(
    (checked: boolean) => {
      analytics.track(ASSIGNEEPICKER_ANALYTICS.INCLUDE_GROUP_MEMBERS_CHANGED, {
        product_id: currentProductId,
        project_id: currentProjectId,
        tool_id: currentToolId,
        account_id: currentAccountId,
        include_group_members: checked,
      });

      setIsIncludeMembersChecked(checked);
    },
    [
      currentAccountId,
      currentProductId,
      currentProjectId,
      currentToolId,
      setIsIncludeMembersChecked,
    ],
  );

  const { usersByCompany, usersByRole, userCompanies, userRoles } = useMemo(
    () => createHelperMappings(userOptionValueGetter, users),
    [userOptionValueGetter, users],
  );

  useEffect(() => {
    const newSelectedCount = getTotalSelectedCount(
      isIncludeMembersChecked,
      dropdownSelectedItems,
      userCompanies,
      userRoles,
      usersByCompany,
      usersByRole,
    );

    setSelectedCount(newSelectedCount);
    onSelectedCountChange?.(newSelectedCount);
  }, [
    isIncludeMembersChecked,
    dropdownSelectedItems,
    userCompanies,
    userRoles,
    usersByCompany,
    usersByRole,
    onSelectedCountChange,
  ]);

  return (
    <>
      {/** Display checkbox option */}
      <Footer style={{ justifyContent: 'left' }}>
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="left"
        >
          <Checkbox
            id="includeMembersCheckbox"
            checked={isIncludeMembersChecked}
            onChange={(state) =>
              handleIncludeMembersCheckedChange(state === true)
            }
          />
          <label
            htmlFor="includeMembersCheckbox"
            style={{
              paddingLeft: '7px',
              ...theme.typography.bodySmall,
            }}
          >
            {checkboxLabel || t('INCLUDE_GROUP_MEMBERS')}
          </label>
          {!checkboxLabel && (
            <InfoPopover
              data-testid="includeMembersTextTooltip"
              style={{ display: 'flex', marginLeft: 10 }}
              placement="top"
              variant={InfoPopover.VARIANTS.DARK}
              triggerTarget="children"
              message={t('INCLUDE_GROUP_MEMBERS_TOOLTIP_TEXT')}
            />
          )}
        </Box>
      </Footer>
      {/** Display selection count */}
      <Footer>
        {renderSelectedCount({ count: selectedCount })}
        <Box cursor={isClearDisabled ? 'not-allowed' : 'initial'}>
          <ClearButton
            style={isClearDisabled ? { pointerEvents: 'none' } : undefined}
            disabled={isClearDisabled}
            onBlur={onBlur}
            onClick={() => {
              onClear?.();
              setSelectedCount(0);
            }}
            onMouseDown={(e) => {
              // Prevent the input from losing focus when the clear button is clicked
              e.preventDefault();
            }}
          />
        </Box>
      </Footer>
    </>
  );
};

export const AssigneePickerIncludeMembersFooter = WithAlloySuspense(
  AssigneePickerIncludeMembersFooterComponent,
);
